import type { HeardPerson, SessionSuggestion, SessionUse, TalkSpan, TeamMember, VoiceFileInfo, VoiceSessionInfo, VoiceSessionTrackInfo } from "@69ly/shared";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { ConfirmDialog } from "../components/ConfirmDialog.tsx";
import { Icon } from "../components/Icon.tsx";
import { Menu } from "../components/Menu.tsx";
import { Select } from "../components/Select.tsx";
import { SoloPlayer } from "../components/SoloPlayer.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { useTrackPlayer, type SoloTrack, type TrackPlayer } from "../demo/track-player.ts";
import { ApiFailure } from "../lib/api.ts";
import {
  sessionTrackUrl,
  useDeleteVoiceFile,
  useDeleteVoiceSession,
  useTrackTalk,
  useUpdateVoiceFile,
  useUseRecording,
  useVoiceFiles,
  useVoiceSessions,
  voiceFileUrl,
} from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";
import { useMe, useTeam } from "../lib/queries.ts";
import { mapName } from "./Matches.tsx";

/** The open recording is in the address (?rec=<id>): Back closes the drawer, a reload keeps it. */
const OPEN = "rec";
/** The open uploaded file (?file=<id>), the same way (0.25.0). */
const OPEN_FILE = "file";

// ————— Formatting —————

function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const day = (iso: string) => new Date(iso).toLocaleDateString(lang, { weekday: "short", day: "numeric", month: "short" });
  const time = (iso: string | number) => new Date(iso).toLocaleTimeString(lang, { hour: "numeric", minute: "2-digit" });
  const length = (seconds: number) => {
    const m = Math.max(1, Math.round(seconds / 60));
    return m < 60 ? t("recordings.minutes", { m }) : t("recordings.hours", { h: Math.floor(m / 60), m: String(m % 60).padStart(2, "0") });
  };
  const size = (bytes: number) => t("recordings.mb", { n: Math.max(1, Math.round(bytes / 1e6)) });
  const month = (key: string) => new Date(`${key}-01T12:00:00`).toLocaleDateString(lang, { month: "long", year: "numeric" });
  return { day, time, length, size, month };
}

/** Seconds from start to end (or to now, still recording). */
const sessionSeconds = (s: VoiceSessionInfo) => ((s.endedAt ? Date.parse(s.endedAt) : Date.now()) - Date.parse(s.startedAt)) / 1000;
const sessionBytes = (s: VoiceSessionInfo) => s.tracks.reduce((n, x) => n + x.bytes, 0);
/** "2026-11", in local time: the month filter. */
const monthKey = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const viewerLink = (demoId: string) => `/demos/${demoId}?m=voice`;

// ————— The tab —————

/** The kind filter (0.25.0): the bot's recordings, the files players uploaded, or both. */
type Kind = "" | "bot" | "files";
type Item = { kind: "bot"; at: string; session: VoiceSessionInfo } | { kind: "file"; at: string; file: VoiceFileInfo };

/**
 * Matches › Recordings (0.24.0): what the Discord bot recorded, when, who, and which
 * maps use it; with the files players uploaded since 0.25.0.
 */
export function RecordingsView({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const sessions = useVoiceSessions(teamId);
  const files = useVoiceFiles(teamId);
  const team = useTeam(teamId);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const pushed = useRef(false);
  const month = params.get("month") ?? "";
  const who = params.get("who") ?? "";
  const asked = params.get("kind");
  const kind: Kind = asked === "bot" || asked === "files" ? asked : "";
  const openId = params.get(OPEN);
  const openFile = params.get(OPEN_FILE);

  const set = (key: string, value: string | null, push = false) => {
    const n = new URLSearchParams(window.location.search);
    if (value === null || value === "") n.delete(key);
    else n.set(key, value);
    setParams(n, { replace: !push });
  };
  // A drawer is one step in the history: Back closes it.
  const open = (key: string, id: string) => {
    const first = !openId && !openFile;
    pushed.current = first;
    set(key, id, first);
  };
  const close = (key: string) => {
    if (pushed.current) {
      pushed.current = false;
      void navigate(-1);
    } else set(key, null);
  };

  const all = useMemo<Item[]>(
    () =>
      [...(sessions.data ?? []).map((s) => ({ kind: "bot" as const, at: s.startedAt, session: s })), ...(files.data ?? []).map((x) => ({ kind: "file" as const, at: x.createdAt, file: x }))].sort(
        (a, b) => Date.parse(b.at) - Date.parse(a.at),
      ),
    [sessions.data, files.data],
  );
  const months = useMemo(() => [...new Set(all.map((x) => monthKey(x.at)))], [all]);
  const peopleOf = (x: Item) => (x.kind === "bot" ? x.session.tracks.map((p) => p.name) : x.file.uploadedBy ? [x.file.uploadedBy.name] : []);
  const people = useMemo(() => [...new Set(all.flatMap(peopleOf))].sort((a, b) => a.localeCompare(b)), [all]);
  const counts = { "": all.length, bot: all.filter((x) => x.kind === "bot").length, files: all.filter((x) => x.kind === "file").length };
  const shown = all.filter((x) => (!kind || (kind === "bot") === (x.kind === "bot")) && (!month || monthKey(x.at) === month) && (!who || peopleOf(x).includes(who)));
  const seconds = shown.reduce((n, x) => n + (x.kind === "bot" ? sessionSeconds(x.session) : (x.file.durationSeconds ?? 0)), 0);
  const members = team.data?.members ?? [];
  const opened = openId ? sessions.data?.find((s) => s.id === openId) : undefined;
  const openedFile = openFile ? files.data?.find((x) => x.id === openFile) : undefined;

  if (sessions.isPending || files.isPending) return <Loading />;
  const failed = sessions.error ?? files.error;
  if (failed)
    return (
      <ErrorState
        message={errorText(t, failed)}
        onRetry={() => {
          void sessions.refetch();
          void files.refetch();
        }}
      />
    );

  if (!all.length) {
    return (
      <div className="card rec-empty">
        <span className="rec-empty-icon" aria-hidden="true">
          <Icon name="mic" size={20} />
        </span>
        <h2 className="section-title">{t("recordings.emptyTitle")}</h2>
        <p className="muted">
          {t("recordings.emptyStart")} <code>/record start</code>
          {t("recordings.emptyStop")} <code>/record stop</code>
          {t("recordings.emptyEnd")}
        </p>
        <p className="muted small">{t("recordings.emptyFiles")}</p>
        <Link to="/settings/team#discord" className="btn btn-secondary btn-small">
          {t("recordings.emptyLink")}
        </Link>
      </div>
    );
  }

  return (
    <div className="rec-view">
      <div className="rec-filters">
        <div className="segmented segmented-small rec-kind" role="group" aria-label={t("recordings.kind")}>
          {(["", "bot", "files"] as const).map((k) => (
            <button key={k || "all"} type="button" className="seg" aria-pressed={kind === k} onClick={() => set("kind", k)}>
              {t(`recordings.kinds.${k || "all"}`)} <span className="muted">{counts[k]}</span>
            </button>
          ))}
        </div>
        <Select
          label={t("recordings.month")}
          value={month}
          placeholder={t("recordings.anyMonth")}
          groups={[{ options: [{ value: "", label: t("recordings.anyMonth") }, ...months.map((m) => ({ value: m, label: f.month(m) }))] }]}
          onChange={(v) => set("month", v)}
        />
        <Select
          label={t("recordings.player")}
          value={who}
          placeholder={t("recordings.anyPlayer")}
          groups={[{ options: [{ value: "", label: t("recordings.anyPlayer") }, ...people.map((p) => ({ value: p, label: p }))] }]}
          onChange={(v) => set("who", v)}
        />
        <span className="muted small" role="status">
          {t(kind === "bot" ? "recordings.count" : kind === "files" ? "recordings.countFiles" : "recordings.countAll", { count: shown.length, length: f.length(seconds) })}
        </span>
      </div>
      {shown.length === 0 ? (
        <p className="muted">{t("recordings.noneFiltered")}</p>
      ) : (
        <ul className="rec-list">
          {shown.map((x) =>
            x.kind === "bot" ? (
              <RecordingRow key={x.session.id} teamId={teamId} session={x.session} members={members} onOpen={() => open(OPEN, x.session.id)} />
            ) : (
              <FileRow key={x.file.id} file={x.file} members={members} onOpen={() => open(OPEN_FILE, x.file.id)} />
            ),
          )}
        </ul>
      )}
      {opened && <RecordingDrawer key={opened.id} teamId={teamId} session={opened} members={members} onClose={() => close(OPEN)} />}
      {openedFile && <FileDrawer key={openedFile.id} file={openedFile} onClose={() => close(OPEN_FILE)} />}
    </div>
  );
}

function whenText(f: ReturnType<typeof useFormat>, s: VoiceSessionInfo) {
  return `${f.day(s.startedAt)} · ${f.time(s.startedAt)}–${s.endedAt ? f.time(s.endedAt) : "…"}`;
}

const avatarOf = (members: TeamMember[], userId: string | null) => members.find((m) => m.userId === userId)?.avatarUrl ?? null;

function RecordingRow({ teamId, session: s, members, onOpen }: { teamId: string; session: VoiceSessionInfo; members: TeamMember[]; onOpen: () => void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const del = useDeleteVoiceSession(teamId);
  const [asking, setAsking] = useState(false);
  const startedBy = members.find((m) => m.name === s.startedBy);
  return (
    <li className="card rec-row" aria-label={whenText(f, s)}>
      <div className="rec-when">
        <button type="button" className="rec-title" onClick={onOpen}>
          {whenText(f, s)}
          {!s.endedAt && <span className="rec-live">{t("recordings.live")}</span>}
        </button>
        <div className="rec-meta">
          <span>
            <Icon name="clock" size={13} /> {f.length(sessionSeconds(s))}
          </span>
          <span>
            <Icon name="mic" size={13} /> {s.channelName}
          </span>
          <span>{f.size(sessionBytes(s))}</span>
        </div>
        {s.startedBy && (
          <div className="rec-meta">
            <span className="muted">{t("recordings.startedBy")}</span>
            <Avatar name={s.startedBy} url={startedBy?.avatarUrl} size={18} />
            <span>{s.startedBy}</span>
          </div>
        )}
        <People tracks={s.tracks} heard={s.heard} members={members} />
      </div>
      <Uses teamId={teamId} session={s} />
      <div className="rec-actions">
        <button type="button" className="btn btn-quiet btn-small" onClick={onOpen}>
          {t("recordings.details")}
        </button>
        {s.canDelete && <Menu label={t("recordings.more")} items={[{ label: t("recordings.delete"), danger: true, onSelect: () => setAsking(true) }]} />}
      </div>
      {asking && (
        <DeleteRecording
          busy={del.isPending}
          error={del.error ? errorText(t, del.error) : null}
          used={s.usedOn.length}
          onConfirm={() => del.mutate(s.id, { onSuccess: () => setAsking(false) })}
          onCancel={() => {
            del.reset();
            setAsking(false);
          }}
        />
      )}
    </li>
  );
}

function DeleteRecording({ busy, error, used, onConfirm, onCancel }: { busy: boolean; error: string | null; used: number; onConfirm: () => void; onCancel: () => void }) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog title={t("recordings.deleteTitle")} confirm={t("recordings.deleteConfirm")} cancel={t("common.cancel")} danger busy={busy} onConfirm={onConfirm} onCancel={onCancel}>
      <p className="muted">{used ? t("recordings.deleteUsed", { count: used }) : t("recordings.deleteHelp")}</p>
      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </ConfirmDialog>
  );
}

const HEARD_TEXT = { not_linked: "recordings.why.not_linked", no_consent: "recordings.why.no_consent", not_member: "recordings.why.not_member" } as const;
const HEARD_LONG = { not_linked: "recordings.whyLong.not_linked", no_consent: "recordings.whyLong.no_consent", not_member: "recordings.whyLong.not_member" } as const;

/** Who was recorded, then who was there but not recorded (dashed), with why. */
function People({ tracks, heard, members, long = false }: { tracks: VoiceSessionTrackInfo[]; heard: HeardPerson[]; members: TeamMember[]; long?: boolean }) {
  const { t } = useTranslation();
  return (
    <ul className="rec-people" aria-label={t("recordings.people")}>
      {!long &&
        tracks.map((x) => (
          <li key={x.id} className="rec-person">
            <Avatar name={x.name} url={avatarOf(members, x.userId)} size={20} />
            <span>{x.name}</span>
          </li>
        ))}
      {heard.map((p) => (
        <li key={p.discordId} className="rec-person is-missing" title={t("recordings.notRecorded", { why: t(HEARD_LONG[p.why]) })}>
          <Avatar name={p.name} size={20} />
          <span>
            {p.name} · {t((long ? HEARD_LONG : HEARD_TEXT)[p.why])}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ————— Used on / suggested —————

/** Where the recording is used, or the matches of that evening to use it on. */
function Uses({ teamId, session: s, inDrawer = false }: { teamId: string; session: VoiceSessionInfo; inDrawer?: boolean }) {
  const { t } = useTranslation();
  const use = useUseRecording(teamId);
  const [asking, setAsking] = useState<{ demoId: string; message: string } | null>(null);
  const attach = (demoId: string, replace = false) =>
    use.mutate(
      { demoId, sessionId: s.id, replace },
      {
        onSuccess: () => setAsking(null),
        onError: (e) => {
          if (e instanceof ApiFailure && e.code === "other_source") setAsking({ demoId, message: e.message });
        },
      },
    );
  const Heading = inDrawer ? "h3" : "span";
  return (
    <div className="rec-use">
      {s.usedOn.length > 0 ? (
        <>
          <Heading className="eyebrow">{t("recordings.usedOn")}</Heading>
          <UsedChips uses={s.usedOn} />
        </>
      ) : s.suggested.length > 0 ? (
        <>
          <Heading className="eyebrow">{inDrawer ? t("recordings.usedOn") : t("recordings.suggested")}</Heading>
          {inDrawer && <p className="muted small">{t("recordings.suggestedHelp")}</p>}
          <SuggestedChips suggested={s.suggested} busy={use.isPending} onUse={(demoId) => attach(demoId)} />
        </>
      ) : (
        <>
          <Heading className="eyebrow">{t("recordings.notUsed")}</Heading>
          <span className="muted small">{t("recordings.notUsedHelp")}</span>
        </>
      )}
      {use.error && !(use.error instanceof ApiFailure && use.error.code === "other_source") && (
        <p className="field-error" role="alert">
          {errorText(t, use.error)}
        </p>
      )}
      {asking && (
        <ConfirmDialog
          eyebrow={t("recordings.oneSource")}
          title={t("recordings.replaceTitle")}
          confirm={t("recordings.replaceConfirm")}
          cancel={t("recordings.replaceKeep")}
          busy={use.isPending}
          onConfirm={() => attach(asking.demoId, true)}
          onCancel={() => setAsking(null)}
        >
          <p className="muted">{asking.message}</p>
          <p className="muted small">{t("recordings.replaceHelp")}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function UsedChips({ uses }: { uses: SessionUse[] }) {
  const { t } = useTranslation();
  return (
    <ul className="rec-chips">
      {uses.map((u) => (
        <li key={u.demoId}>
          <Link to={viewerLink(u.demoId)} className="rec-chip is-used">
            <Icon name="check" size={13} />
            {u.matchTitle}
            <span className="muted">· {t("recordings.mapN", { n: u.mapNumber, map: mapName(u.map) })}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function SuggestedChips({ suggested, busy, onUse }: { suggested: SessionSuggestion[]; busy: boolean; onUse: (demoId: string) => void }) {
  const { t } = useTranslation();
  return (
    <ul className="rec-chips">
      {suggested.flatMap((m) =>
        m.maps.map((g) => (
          <li key={g.demoId}>
            <button type="button" className="rec-chip" disabled={busy} onClick={() => onUse(g.demoId)}>
              <Icon name="play" size={12} />
              {t("recordings.useOn", { title: m.title })}
              <span className="muted">· {t("recordings.mapN", { n: g.mapNumber, map: mapName(g.map) })}</span>
            </button>
          </li>
        )),
      )}
    </ul>
  );
}

// ————— The drawer —————

function RecordingDrawer({ teamId, session: s, members, onClose }: { teamId: string; session: VoiceSessionInfo; members: TeamMember[]; onClose: () => void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const me = useMe();
  const dialog = useRef<HTMLDialogElement>(null);
  const player = useTrackPlayer();
  const del = useDeleteVoiceSession(teamId);
  const [asking, setAsking] = useState(false);
  const start = Date.parse(s.startedAt);
  const span = Math.max(1, sessionSeconds(s));

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (typeof d.showModal === "function" && !d.open) d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);

  const stopped = s.endedBy ? t("recordings.stoppedBy", { name: s.endedBy }) : s.endedAt ? t("recordings.stoppedEmpty") : t("recordings.stillRecording");
  const facts = [s.channelName, f.length(sessionSeconds(s)), f.size(sessionBytes(s)), s.startedBy ? t("recordings.startedByName", { name: s.startedBy }) : null, stopped].filter(Boolean).join(" · ");

  return (
    <dialog
      ref={dialog}
      className="rec-drawer"
      aria-labelledby={`rec-${s.id}`}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="rec-drawer-panel">
        <div className="rec-drawer-head">
          <div>
            <p className="eyebrow">{t("recordings.recording")}</p>
            <h2 id={`rec-${s.id}`} className="rec-drawer-title">
              {whenText(f, s)}
            </h2>
            <p className="muted small">{facts}</p>
          </div>
          <button type="button" className="btn btn-quiet btn-icon" aria-label={t("viewer.close")} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        <section className="rec-drawer-section">
          <Uses teamId={teamId} session={s} inDrawer />
        </section>

        <section className="rec-drawer-section" aria-labelledby={`tracks-${s.id}`}>
          <h3 id={`tracks-${s.id}`} className="eyebrow">
            {t("recordings.tracks", { count: s.tracks.length })}
          </h3>
          <p className="muted small">{t("recordings.tracksHelp")}</p>
          <ul className="rec-tracks">
            {s.tracks.map((x) => (
              <TrackRow
                key={x.id}
                sessionId={s.id}
                track={x}
                offset={(Date.parse(x.startedAt) - start) / 1000}
                span={span}
                startIso={s.startedAt}
                endIso={s.endedAt}
                avatar={avatarOf(members, x.userId)}
                me={!!me.data?.user && x.userId === me.data.user.id}
                player={player}
              />
            ))}
          </ul>
        </section>

        {s.heard.length > 0 && (
          <section className="rec-drawer-section">
            <h3 className="eyebrow">{t("recordings.notRecordedTitle")}</h3>
            <People tracks={[]} heard={s.heard} members={members} long />
          </section>
        )}

        {s.canDelete && (
          <div className="rec-drawer-foot">
            <button type="button" className="btn btn-quiet btn-small btn-danger" onClick={() => setAsking(true)}>
              <Icon name="trash" size={14} />
              {t("recordings.delete")}
            </button>
            <span className="muted small">{t("recordings.deleteWho")}</span>
          </div>
        )}
      </div>
      {asking && (
        <DeleteRecording
          busy={del.isPending}
          error={del.error ? errorText(t, del.error) : null}
          used={s.usedOn.length}
          onConfirm={() => del.mutate(s.id, { onSuccess: onClose })}
          onCancel={() => {
            del.reset();
            setAsking(false);
          }}
        />
      )}
    </dialog>
  );
}

/** One player: ▶, when they talked across the recording, and where the playhead is. */
function TrackRow({
  sessionId,
  track: x,
  offset,
  span,
  startIso,
  endIso,
  avatar,
  me,
  player,
}: {
  sessionId: string;
  track: VoiceSessionTrackInfo;
  offset: number;
  span: number;
  startIso: string;
  endIso: string | null;
  avatar: string | null;
  me: boolean;
  player: TrackPlayer;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const fetched = useTrackTalk(sessionId, x.id, x.talk === null);
  const talk: TalkSpan[] = x.talk ?? fetched.data ?? [];
  const solo: SoloTrack = { id: x.id, url: sessionTrackUrl(sessionId, x.id), contentType: "audio/ogg", bytes: x.bytes, duration: x.durationSeconds };
  const mine = player.id === x.id;
  const playing = mine && player.playing;
  const pct = (seconds: number) => `${Math.min(100, Math.max(0, ((offset + seconds) / span) * 100))}%`;
  // From a point on the bar (the whole recording) to the track's own second.
  const toTrack = (fraction: number) => Math.min(x.durationSeconds, Math.max(0, fraction * span - offset));
  const jump = (e: PointerEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    player.play(solo, toTrack((e.clientX - box.left) / box.width));
  };
  const key = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === "ArrowRight" ? 10 : e.key === "ArrowLeft" ? -10 : 0;
    if (!step) return;
    e.preventDefault();
    const now = mine ? player.at : 0;
    if (mine) player.seek(now + step);
    else player.play(solo, Math.max(0, now + step));
  };
  const clock = (seconds: number) => f.time(Date.parse(x.startedAt) + seconds * 1000);
  return (
    <li className={`rec-track${playing ? " is-playing" : ""}`}>
      <button
        type="button"
        className={`rec-play${playing ? " is-playing" : ""}`}
        aria-pressed={playing}
        aria-label={t(playing ? "recordings.pause" : "recordings.listen", { name: x.name })}
        onClick={() => player.toggle(solo)}
      >
        <Icon name={playing ? "pause" : "play"} />
      </button>
      <Avatar name={x.name} url={avatar} me={me} size={26} />
      <div className="rec-track-body">
        <b>{x.name}</b>
        <div
          className="rec-talk"
          role="slider"
          tabIndex={0}
          aria-label={t("recordings.timeline", { name: x.name })}
          aria-valuemin={0}
          aria-valuemax={Math.round(x.durationSeconds)}
          aria-valuenow={Math.round(mine ? player.at : 0)}
          aria-valuetext={mine ? clock(player.at) : clock(0)}
          onPointerDown={jump}
          onKeyDown={key}
        >
          <span className="rec-talk-span" style={{ left: pct(0), width: `calc(${pct(x.durationSeconds)} - ${pct(0)})` }} />
          {talk.map(([a, b]) => (
            <i key={a} style={{ left: pct(a), width: `max(2px, calc(${pct(b)} - ${pct(a)}))` }} />
          ))}
          {mine && <b className="rec-head" style={{ left: pct(player.at) }} />}
        </div>
        <div className="rec-ends">
          <span>{mine ? t(playing ? "recordings.playingAt" : "recordings.pausedAt", { time: clock(player.at) }) : f.time(startIso)}</span>
          <span>{endIso ? f.time(endIso) : "…"}</span>
        </div>
      </div>
      <span className="muted small mono rec-track-length">{f.length(x.durationSeconds)}</span>
    </li>
  );
}

// ————— Uploaded files (0.25.0) —————

const FORMATS: Record<string, string> = { "audio/mpeg": "MP3", "audio/ogg": "OGG", "audio/webm": "WebM", "audio/mp4": "M4A", "audio/wav": "WAV", "audio/flac": "FLAC" };

/** A file a player uploaded: its name, who and when, and the map it is on. */
function FileRow({ file: x, members, onOpen }: { file: VoiceFileInfo; members: TeamMember[]; onOpen: () => void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const [asking, setAsking] = useState<"rename" | "delete" | null>(null);
  return (
    <li className="card rec-row is-file" aria-label={x.label}>
      <div className="rec-when">
        <button type="button" className="rec-title" onClick={onOpen}>
          <span className="rec-file-icon" aria-hidden="true">
            <Icon name="upload" size={13} />
          </span>
          {x.label}
        </button>
        <div className="rec-meta">
          <span className="rec-tag">{t("recordings.uploaded")}</span>
          {x.durationSeconds !== null && (
            <span>
              <Icon name="clock" size={13} /> {f.length(x.durationSeconds)}
            </span>
          )}
          <span>{f.size(x.bytes)}</span>
        </div>
        {x.uploadedBy && (
          <div className="rec-meta">
            <span className="muted">{t("recordings.addedBy")}</span>
            <Avatar name={x.uploadedBy.name} url={x.uploadedBy.avatarUrl ?? avatarOf(members, x.uploadedBy.id)} size={18} />
            <span>
              {x.uploadedBy.name} · {f.day(x.createdAt)}
            </span>
          </div>
        )}
      </div>
      <div className="rec-use">
        {x.usedOn.length ? (
          <>
            <span className="eyebrow">{t("recordings.usedOn")}</span>
            <UsedChips uses={x.usedOn} />
          </>
        ) : (
          <>
            <span className="eyebrow">{t("recordings.notUsed")}</span>
            <span className="muted small">{t("recordings.fileNotUsed")}</span>
          </>
        )}
      </div>
      <div className="rec-actions">
        <button type="button" className="btn btn-quiet btn-small" onClick={onOpen}>
          {t("recordings.details")}
        </button>
        {x.canEdit && (
          <Menu
            label={t("recordings.fileMore", { name: x.label })}
            items={[
              { label: t("recordings.renameFile"), onSelect: () => setAsking("rename") },
              { label: t("recordings.deleteFile"), danger: true, onSelect: () => setAsking("delete") },
            ]}
          />
        )}
      </div>
      {asking === "rename" && <RenameFile file={x} onDone={() => setAsking(null)} />}
      {asking === "delete" && <DeleteFile file={x} onDone={() => setAsking(null)} />}
    </li>
  );
}

/** Rename in a small dialog (from the row's menu). */
function RenameFile({ file: x, onDone }: { file: VoiceFileInfo; onDone: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateVoiceFile();
  const [name, setName] = useState(x.label);
  return (
    <ConfirmDialog
      title={t("recordings.renameTitle")}
      confirm={t("common.save")}
      cancel={t("common.cancel")}
      busy={update.isPending}
      confirmDisabled={!name.trim() || name.trim() === x.label}
      onConfirm={() => update.mutate({ id: x.id, label: name }, { onSuccess: onDone })}
      onCancel={onDone}
    >
      <label className="field">
        <span>{t("recordings.fileName")}</span>
        <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </label>
      {update.error && (
        <p className="field-error" role="alert">
          {errorText(t, update.error)}
        </p>
      )}
    </ConfirmDialog>
  );
}

function DeleteFile({ file: x, onDone, onDeleted }: { file: VoiceFileInfo; onDone: () => void; onDeleted?: () => void }) {
  const { t } = useTranslation();
  const del = useDeleteVoiceFile();
  const on = x.usedOn[0];
  return (
    <ConfirmDialog
      title={t("recordings.deleteFileTitle", { name: x.label })}
      confirm={t("recordings.deleteConfirm")}
      cancel={t("common.cancel")}
      danger
      busy={del.isPending}
      onConfirm={() =>
        del.mutate(x.id, {
          onSuccess: () => {
            onDone();
            onDeleted?.();
          },
        })
      }
      onCancel={onDone}
    >
      <p className="muted">{on ? t("recordings.deleteFileUsed", { match: on.matchTitle, map: t("recordings.mapN", { n: on.mapNumber, map: mapName(on.map) }) }) : t("recordings.deleteFileHelp")}</p>
      {del.error && (
        <p className="field-error" role="alert">
          {errorText(t, del.error)}
        </p>
      )}
    </ConfirmDialog>
  );
}

/** An uploaded file on the side: its facts, where it plays, a player; rename in place, delete. */
function FileDrawer({ file: x, onClose }: { file: VoiceFileInfo; onClose: () => void }) {
  const { t } = useTranslation();
  const f = useFormat();
  const dialog = useRef<HTMLDialogElement>(null);
  const player = useTrackPlayer();
  const update = useUpdateVoiceFile();
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(x.label);
  const [asking, setAsking] = useState(false);
  const solo: SoloTrack = { id: x.id, url: voiceFileUrl(x.id), contentType: x.contentType, bytes: x.bytes, duration: x.durationSeconds ?? undefined };
  const at = player.id === x.id ? player.at : 0;

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (typeof d.showModal === "function" && !d.open) d.showModal();
    else d.setAttribute("open", "");
    return () => {
      if (d.open && typeof d.close === "function") d.close();
    };
  }, []);
  // The first browser that reads the file tells its length, for the list.
  const told = useRef(false);
  useEffect(() => {
    if (told.current || x.durationSeconds !== null || player.id !== x.id || !player.duration) return;
    told.current = true;
    update.mutate({ id: x.id, durationSeconds: Math.round(player.duration * 10) / 10 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.duration, player.id]);
  // Ready to play: the length shows before the first ▶.
  useEffect(() => {
    player.cue(solo, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [x.id]);

  const length = x.durationSeconds ?? player.duration;
  const facts = [
    FORMATS[x.contentType] ?? x.contentType,
    length ? f.length(length) : null,
    f.size(x.bytes),
    x.uploadedBy ? t("recordings.addedByOn", { name: x.uploadedBy.name, date: f.day(x.createdAt) }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <dialog
      ref={dialog}
      className="rec-drawer"
      aria-labelledby={`file-${x.id}`}
      onCancel={(e) => {
        e.preventDefault();
        if (renaming) setRenaming(false);
        else onClose();
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="rec-drawer-panel">
        <div className="rec-drawer-head">
          <div className="file-head">
            <p className="eyebrow">{t("recordings.uploadedFile")}</p>
            {renaming ? (
              <form
                className="file-rename"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (name.trim()) update.mutate({ id: x.id, label: name }, { onSuccess: () => setRenaming(false) });
                }}
              >
                <input value={name} maxLength={60} aria-label={t("recordings.fileName")} autoFocus onChange={(e) => setName(e.target.value)} />
                <button type="submit" className="btn btn-primary btn-small" disabled={update.isPending || !name.trim() || name.trim() === x.label}>
                  {t("common.save")}
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-small"
                  onClick={() => {
                    setName(x.label);
                    setRenaming(false);
                  }}
                >
                  {t("common.cancel")}
                </button>
              </form>
            ) : (
              <div className="file-title">
                <h2 id={`file-${x.id}`} className="rec-drawer-title">
                  {x.label}
                </h2>
                {x.canEdit && (
                  <button type="button" className="btn btn-quiet btn-icon btn-small" aria-label={t("recordings.renameFile")} title={t("recordings.renameFile")} onClick={() => setRenaming(true)}>
                    <Icon name="edit" size={15} />
                  </button>
                )}
              </div>
            )}
            <p className="muted small">{facts}</p>
          </div>
          <button type="button" className="btn btn-quiet btn-icon" aria-label={t("viewer.close")} onClick={onClose}>
            <Icon name="close" />
          </button>
        </div>

        <section className="rec-drawer-section">
          <h3 className="eyebrow">{x.usedOn.length ? t("recordings.usedOn") : t("recordings.notUsed")}</h3>
          {x.usedOn.length ? <UsedChips uses={x.usedOn} /> : null}
          <p className="muted small">{x.usedOn.length ? t("recordings.fileUsedHelp") : t("recordings.fileNotUsed")}</p>
        </section>

        <section className="rec-drawer-section" aria-labelledby={`listen-${x.id}`}>
          <h3 id={`listen-${x.id}`} className="eyebrow">
            {t("recordings.listenTitle")}
          </h3>
          <div className="file-player">
            <SoloPlayer player={player} track={solo} at={at} testId="file-at" />
          </div>
          <p className="muted small">{t("recordings.fileOneMix")}</p>
        </section>

        {x.canEdit && (
          <div className="rec-drawer-foot">
            <button type="button" className="btn btn-quiet btn-small btn-danger" onClick={() => setAsking(true)}>
              <Icon name="trash" size={14} />
              {t("recordings.deleteFile")}
            </button>
            <span className="muted small">{t("recordings.deleteFileWho")}</span>
          </div>
        )}
      </div>
      {asking && <DeleteFile file={x} onDone={() => setAsking(false)} onDeleted={onClose} />}
    </dialog>
  );
}
